import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Clock,
  Palmtree,
  CalendarOff,
  Bell,
  LogOut,
  ArrowLeft,
  Building2,
  Send,
  Thermometer,
  Briefcase,
  XSquare,
  MessageSquare,
  Users,
  ArrowLeftRight,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { format, addDays, startOfWeek, parseISO, isBefore, startOfDay } from 'date-fns';
import NotificationPanel from '@/components/NotificationPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeRoster = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => {
    const now = new Date();
    return startOfWeek(now, { weekStartsOn: 1 }); // Monday
  });
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);
  const [mySwaps, setMySwaps] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [viewTeam, setViewTeam] = useState(false);
  
  // Request dialog
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [requestForm, setRequestForm] = useState({
    requested_start_time: '09:00',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Shift swap dialog
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [showSwapsListDialog, setShowSwapsListDialog] = useState(false);
  const [selectedSwapEntry, setSelectedSwapEntry] = useState(null);
  const [swapTargetUserId, setSwapTargetUserId] = useState('');
  const [swapSubmitting, setSwapSubmitting] = useState(false);

  // Annual leave dialog
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [myLeaveRequests, setMyLeaveRequests] = useState([]);
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch team data (view_team=true) to enable swap functionality
      // The UI will filter what to display based on viewTeam state
      const [rosterRes, requestsRes, notifRes, swapsRes, leaveRes] = await Promise.all([
        axios.get(`${API}/roster`, { params: { week_start: format(weekStart, 'yyyy-MM-dd'), view_team: true } }),
        axios.get(`${API}/roster/my-requests`),
        axios.get(`${API}/notifications/unread-count`),
        axios.get(`${API}/roster/shift-swaps`).catch(() => ({ data: [] })),
        axios.get(`${API}/annual-leave/my-requests`).catch(() => ({ data: [] }))
      ]);
      setRoster(rosterRes.data);
      setMyRequests(requestsRes.data);
      setMySwaps(swapsRes.data);
      setUnreadCount(notifRes.data.count);
      setMyLeaveRequests(leaveRes.data);
    } catch (error) {
      console.error('Failed to fetch roster:', error);
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const navigateWeek = (direction) => {
    setWeekStart(prev => addDays(prev, direction * 7));
  };

  const getDateForDay = (dayIndex) => {
    return addDays(weekStart, dayIndex);
  };

  const getEntryForDay = (dayIndex, employeeId = null) => {
    if (!roster?.entries) return null;
    const date = format(getDateForDay(dayIndex), 'yyyy-MM-dd');
    const userId = employeeId || user?.id;
    return roster.entries.find(e => e.date === date && e.user_id === userId);
  };

  const getRequestForDay = (dayIndex) => {
    const date = format(getDateForDay(dayIndex), 'yyyy-MM-dd');
    return myRequests.find(r => r.date === date && r.status === 'pending');
  };

  const canRequestChange = (dayIndex) => {
    const date = getDateForDay(dayIndex);
    // Can only request changes for future dates
    return !isBefore(date, startOfDay(new Date()));
  };

  const openRequestDialog = (dayIndex) => {
    const date = format(getDateForDay(dayIndex), 'yyyy-MM-dd');
    const existingEntry = getEntryForDay(dayIndex);
    
    setSelectedDate({
      date,
      dayIndex,
      existing: existingEntry
    });
    setRequestForm({
      requested_start_time: existingEntry?.start_time || '09:00',
      reason: ''
    });
    setShowRequestDialog(true);
  };

  const handleSubmitRequest = async () => {
    if (!selectedDate) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/roster/request`, {
        date: selectedDate.date,
        requested_start_time: requestForm.requested_start_time,
        reason: requestForm.reason
      });
      toast.success('Request submitted!');
      setShowRequestDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Annual leave functions
  const openLeaveDialog = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    setLeaveForm({
      start_date: today,
      end_date: today,
      reason: ''
    });
    setShowLeaveDialog(true);
  };

  const handleSubmitLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) return;
    
    setLeaveSubmitting(true);
    try {
      await axios.post(`${API}/annual-leave/request`, leaveForm);
      toast.success('Annual leave request submitted!');
      setShowLeaveDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setLeaveSubmitting(false);
    }
  };

  const handleCancelLeave = async (requestId) => {
    try {
      await axios.delete(`${API}/annual-leave/requests/${requestId}`);
      toast.success('Leave request cancelled');
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    }
  };

  // Shift swap functions
  const openSwapDialog = (entry) => {
    setSelectedSwapEntry(entry);
    setSwapTargetUserId('');
    setShowSwapDialog(true);
  };

  const handleSubmitSwap = async () => {
    if (!selectedSwapEntry || !swapTargetUserId) return;
    
    setSwapSubmitting(true);
    try {
      await axios.post(`${API}/roster/shift-swap`, {
        roster_entry_id: selectedSwapEntry.id,
        target_user_id: swapTargetUserId
      });
      toast.success('Swap request sent! Waiting for coworker approval.');
      setShowSwapDialog(false);
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request swap');
    } finally {
      setSwapSubmitting(false);
    }
  };

  const handleRespondToSwap = async (swapId, action) => {
    try {
      await axios.put(`${API}/roster/shift-swaps/${swapId}/coworker-response?action=${action}`);
      toast.success(action === 'approve' ? 'Swap approved! Waiting for manager.' : 'Swap declined.');
      fetchRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  // Get coworkers who can swap (exclude self)
  const getSwappableCoworkers = () => {
    if (!roster?.employees) return [];
    return roster.employees.filter(e => e.id !== user?.id);
  };

  // Get pending swaps where user is the target
  const getPendingSwapsForMe = () => {
    return mySwaps.filter(s => s.target_user_id === user?.id && s.status === 'pending_coworker');
  };

  const renderCell = (dayIndex, employeeId = null) => {
    const entry = getEntryForDay(dayIndex, employeeId);
    const pendingRequest = employeeId ? null : getRequestForDay(dayIndex);
    const canRequest = employeeId ? false : canRequestChange(dayIndex);
    const isOwnEntry = !employeeId || employeeId === user?.id;
    
    const cellStyles = {
      shift: 'bg-blue-50 border-blue-200',
      holiday: 'bg-amber-50 border-amber-200',
      holiday_worked: 'bg-amber-50 border-amber-200',
      rdo: 'bg-purple-50 border-purple-200',
      sick: 'bg-red-50 border-red-200',
      leave: 'bg-green-50 border-green-200',
      annual_leave: 'bg-emerald-50 border-emerald-200',
      off: 'bg-slate-100 border-slate-300',
      day_off: 'bg-slate-100 border-slate-300'
    };

    const textStyles = {
      shift: 'text-blue-700',
      holiday: 'text-amber-700',
      holiday_worked: 'text-amber-700',
      rdo: 'text-purple-700',
      sick: 'text-red-700',
      leave: 'text-green-700',
      annual_leave: 'text-emerald-700',
      off: 'text-slate-600',
      day_off: 'text-slate-600'
    };

    const icons = {
      shift: <Clock className="w-4 h-4 sm:w-5 sm:h-5" />,
      holiday: <Palmtree className="w-4 h-4 sm:w-5 sm:h-5" />,
      holiday_worked: <Palmtree className="w-4 h-4 sm:w-5 sm:h-5" />,
      rdo: <CalendarOff className="w-4 h-4 sm:w-5 sm:h-5" />,
      sick: <Thermometer className="w-4 h-4 sm:w-5 sm:h-5" />,
      leave: <Briefcase className="w-4 h-4 sm:w-5 sm:h-5" />,
      annual_leave: <Palmtree className="w-4 h-4 sm:w-5 sm:h-5" />,
      off: <XSquare className="w-4 h-4 sm:w-5 sm:h-5" />,
      day_off: <XSquare className="w-4 h-4 sm:w-5 sm:h-5" />
    };

    const labels = {
      shift: null,
      holiday: 'Holiday',
      holiday_worked: 'Holiday',
      rdo: 'RDO',
      sick: 'Sick',
      leave: 'Leave',
      annual_leave: 'Annual',
      off: 'Day Off',
      day_off: 'Day Off'
    };

    return (
      <div className={`relative p-1.5 sm:p-3 rounded-lg border-2 min-h-[60px] sm:min-h-[90px] flex flex-col items-center justify-center gap-0.5 sm:gap-1 ${
        entry ? (cellStyles[entry.entry_type] || cellStyles.shift) : 'bg-slate-50 border-slate-200'
      }`}>
        {entry ? (
          <>
            <div className={textStyles[entry.entry_type] || textStyles.shift}>
              {icons[entry.entry_type] || icons.shift}
            </div>
            {entry.entry_type === 'shift' || entry.entry_type === 'holiday_worked' ? (
              <p className={`text-sm sm:text-lg font-mono font-bold ${textStyles[entry.entry_type]}`}>
                {entry.start_time}
              </p>
            ) : (
              <p className={`text-[10px] sm:text-xs font-semibold text-center ${textStyles[entry.entry_type] || textStyles.shift}`}>
                {labels[entry.entry_type] || entry.entry_type.replace(/_/g, ' ')}
              </p>
            )}
            {isOwnEntry && entry.comment && (
              <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500" title={entry.comment}>
                <MessageSquare className="w-3 h-3" />
                <span className="truncate max-w-[50px]">{entry.comment}</span>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-slate-400">-</p>
        )}
        
        {/* Request button or pending badge - only for own entries */}
        {isOwnEntry && pendingRequest ? (
          <Badge className="absolute bottom-2 bg-amber-500 text-white text-xs">
            Request Pending
          </Badge>
        ) : canRequest && entry?.entry_type === 'shift' && (
          <div className="absolute bottom-1 flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-6"
              onClick={() => openRequestDialog(dayIndex)}
              data-testid={`request-btn-${dayIndex}`}
            >
              Request
            </Button>
            {getSwappableCoworkers().length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6 text-orange-600"
                onClick={() => openSwapDialog(entry)}
                data-testid={`swap-btn-${dayIndex}`}
                title="Request shift swap"
              >
                <ArrowLeftRight className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Mobile-optimized cell renderer for "My Schedule" view
  const renderCellMobile = (dayIndex) => {
    const entry = getEntryForDay(dayIndex);
    const pendingRequest = getRequestForDay(dayIndex);
    const canRequest = canRequestChange(dayIndex);
    
    const cellStyles = {
      shift: 'bg-blue-50 border-blue-200',
      holiday: 'bg-amber-50 border-amber-200',
      holiday_worked: 'bg-amber-50 border-amber-200',
      rdo: 'bg-purple-50 border-purple-200',
      sick: 'bg-red-50 border-red-200',
      leave: 'bg-green-50 border-green-200',
      annual_leave: 'bg-emerald-50 border-emerald-200',
      off: 'bg-slate-100 border-slate-300',
      day_off: 'bg-slate-100 border-slate-300'
    };

    const textStyles = {
      shift: 'text-blue-700',
      holiday: 'text-amber-700',
      holiday_worked: 'text-amber-700',
      rdo: 'text-purple-700',
      sick: 'text-red-700',
      leave: 'text-green-700',
      annual_leave: 'text-emerald-700',
      off: 'text-slate-600',
      day_off: 'text-slate-600'
    };

    const icons = {
      shift: <Clock className="w-4 h-4" />,
      holiday: <Palmtree className="w-4 h-4" />,
      holiday_worked: <Palmtree className="w-4 h-4" />,
      rdo: <CalendarOff className="w-4 h-4" />,
      sick: <Thermometer className="w-4 h-4" />,
      leave: <Briefcase className="w-4 h-4" />,
      annual_leave: <Palmtree className="w-4 h-4" />,
      off: <XSquare className="w-4 h-4" />,
      day_off: <XSquare className="w-4 h-4" />
    };

    const labels = {
      shift: 'Shift',
      holiday: 'Holiday',
      holiday_worked: 'Holiday',
      rdo: 'RDO',
      sick: 'Sick',
      leave: 'Leave',
      annual_leave: 'Annual Leave',
      off: 'Day Off',
      day_off: 'Day Off'
    };

    return (
      <div className={`flex items-center justify-between p-3 rounded-lg border-2 ${
        entry ? (cellStyles[entry.entry_type] || cellStyles.shift) : 'bg-white border-slate-200'
      }`}>
        <div className="flex items-center gap-2">
          {entry ? (
            <>
              <div className={textStyles[entry.entry_type] || textStyles.shift}>
                {icons[entry.entry_type] || icons.shift}
              </div>
              <div>
                <span className={`font-semibold ${textStyles[entry.entry_type] || textStyles.shift}`}>
                  {entry.entry_type === 'shift' || entry.entry_type === 'holiday_worked' 
                    ? entry.start_time 
                    : (labels[entry.entry_type] || entry.entry_type.replace(/_/g, ' '))}
                </span>
                {entry.comment && (
                  <p className="text-xs text-slate-500 truncate max-w-[120px]">{entry.comment}</p>
                )}
              </div>
            </>
          ) : (
            <span className="text-slate-400">No schedule</span>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          {pendingRequest ? (
            <Badge className="bg-amber-500 text-white text-xs">Pending</Badge>
          ) : canRequest && entry?.entry_type === 'shift' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 px-2"
                onClick={() => openRequestDialog(dayIndex)}
              >
                Request
              </Button>
              {getSwappableCoworkers().length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-orange-600"
                  onClick={() => openSwapDialog(entry)}
                  title="Request shift swap"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const summary = roster?.summaries?.[user?.id] || { shifts: 0, total_hours: 0 };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="text-sm text-slate-500">My Roster</p>
              <p className="font-semibold font-['Manrope']">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setShowNotifications(true)}
              data-testid="notification-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Company Badge */}
        {user?.company_name && (
          <div className="flex justify-center">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border text-sm text-slate-600">
              <Building2 className="w-4 h-4" />
              {user.company_name}
            </div>
          </div>
        )}

        {/* Week Navigation */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <Button variant="outline" size="icon" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <div className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5 text-slate-500" />
                  <span className="text-lg font-semibold font-['Manrope']">
                    {format(weekStart, 'dd/MM/yyyy')} - {format(addDays(weekStart, 6), 'dd/MM/yyyy')}
                  </span>
                </div>
                {!viewTeam && (
                  <div className="flex justify-center gap-4 mt-2 text-sm text-slate-500">
                    <span>{summary.shifts} Shifts</span>
                    <span>•</span>
                    <span>{summary.total_hours} Hours</span>
                  </div>
                )}
              </div>
              
              <Button variant="outline" size="icon" onClick={() => navigateWeek(1)} data-testid="next-week-btn">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* View Toggle */}
        <div className="flex flex-wrap justify-center items-center gap-3">
          <div className="inline-flex rounded-lg border bg-white p-1 shadow-sm">
            <Button
              variant={!viewTeam ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewTeam(false)}
              className={!viewTeam ? "bg-[hsl(243,65%,20%)]" : ""}
              data-testid="my-schedule-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              My Schedule
            </Button>
            <Button
              variant={viewTeam ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewTeam(true)}
              className={viewTeam ? "bg-[hsl(243,65%,20%)]" : ""}
              data-testid="team-schedule-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Full Schedule
            </Button>
          </div>
          
          {/* Pending swaps notification */}
          {getPendingSwapsForMe().length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2 border-orange-300 text-orange-600 hover:bg-orange-50"
              onClick={() => setShowSwapsListDialog(true)}
              data-testid="pending-swaps-btn"
            >
              <ArrowLeftRight className="w-4 h-4" />
              {getPendingSwapsForMe().length} Swap Request{getPendingSwapsForMe().length !== 1 ? 's' : ''}
            </Button>
          )}
          
          {/* Annual Leave Button */}
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-green-300 text-green-600 hover:bg-green-50"
            onClick={openLeaveDialog}
            data-testid="request-leave-btn"
          >
            <Palmtree className="w-4 h-4" />
            Request Leave
          </Button>
        </div>

        {/* Weekly Grid */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              {viewTeam ? <Users className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
              {viewTeam ? 'Team Schedule' : 'My Schedule'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner" />
              </div>
            ) : viewTeam ? (
              /* Team Schedule Grid - with sticky employee column */
              <div className="overflow-x-auto">
                <table className="w-full min-w-[500px] border-separate border-spacing-0">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-100 to-indigo-50">
                      <th className="text-left p-2 sm:p-3 w-20 sm:w-32 sticky left-0 bg-slate-100 z-10 border-b-2 border-indigo-200 font-bold text-slate-700 text-xs sm:text-sm">Employee</th>
                      {days.map((day, idx) => {
                        const isToday = format(getDateForDay(idx), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                        return (
                          <th key={day} className={`text-center p-1 sm:p-2 border-b-2 min-w-[60px] sm:min-w-[80px] ${isToday ? 'bg-gradient-to-b from-indigo-500 to-purple-500 border-indigo-400' : 'bg-slate-50 border-indigo-200'}`}>
                            <p className={`font-bold text-xs sm:text-sm ${isToday ? 'text-white' : 'text-slate-700'}`}>{day}</p>
                            <p className={`text-[10px] sm:text-xs ${isToday ? 'text-white/80' : 'text-slate-400'}`}>{format(getDateForDay(idx), 'MMM d')}</p>
                            {isToday && <Badge className="mt-1 bg-white/20 text-white text-[8px] sm:text-[10px] px-1">TODAY</Badge>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {roster?.employees?.map((emp, empIdx) => (
                      <tr key={emp.id} className={`border-b hover:bg-slate-50/50 transition-colors ${emp.id === user?.id ? 'bg-indigo-50/50' : ''}`}>
                        <td className={`p-1.5 sm:p-2 sticky left-0 z-10 border-r border-slate-200 ${emp.id === user?.id ? 'bg-indigo-50' : 'bg-white'}`}>
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold text-white shadow-sm ${emp.id === user?.id ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}>
                              {emp.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 hidden sm:block">
                              <p className="text-xs font-semibold truncate max-w-[70px]">{emp.name?.split(' ')[0]}</p>
                              {emp.id === user?.id && <Badge className="text-[9px] h-3.5 bg-indigo-100 text-indigo-700">You</Badge>}
                            </div>
                            {/* Mobile: Show "You" badge next to avatar */}
                            {emp.id === user?.id && <Badge className="sm:hidden text-[8px] h-4 bg-indigo-100 text-indigo-700 px-1">You</Badge>}
                          </div>
                        </td>
                        {days.map((_, idx) => (
                          <td key={idx} className="p-0.5 sm:p-1">
                            {renderCell(idx, emp.id)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* My Schedule Grid - Modern card layout */
              <div className="p-4 space-y-4" data-testid="roster-grid">
                {/* Mobile & Desktop: Unified card layout */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
                  {days.map((day, idx) => {
                    const entry = getEntryForDay(idx);
                    const pendingRequest = getRequestForDay(idx);
                    const canRequest = canRequestChange(idx);
                    const isToday = format(getDateForDay(idx), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    
                    const entryStyles = {
                      shift: { bg: 'bg-gradient-to-br from-blue-400 to-blue-600', text: 'text-white', icon: <Clock className="w-5 h-5" />, label: null },
                      holiday: { bg: 'bg-gradient-to-br from-amber-400 to-orange-500', text: 'text-white', icon: <Palmtree className="w-5 h-5" />, label: 'Holiday' },
                      holiday_worked: { bg: 'bg-gradient-to-br from-amber-400 to-orange-500', text: 'text-white', icon: <Palmtree className="w-5 h-5" />, label: 'Holiday' },
                      rdo: { bg: 'bg-gradient-to-br from-purple-400 to-purple-600', text: 'text-white', icon: <CalendarOff className="w-5 h-5" />, label: 'RDO' },
                      sick: { bg: 'bg-gradient-to-br from-red-400 to-red-600', text: 'text-white', icon: <Thermometer className="w-5 h-5" />, label: 'Sick' },
                      leave: { bg: 'bg-gradient-to-br from-emerald-400 to-green-600', text: 'text-white', icon: <Briefcase className="w-5 h-5" />, label: 'Leave' },
                      off: { bg: 'bg-gradient-to-br from-slate-300 to-slate-400', text: 'text-white', icon: <XSquare className="w-5 h-5" />, label: 'Day Off' },
                      day_off: { bg: 'bg-gradient-to-br from-slate-300 to-slate-400', text: 'text-white', icon: <XSquare className="w-5 h-5" />, label: 'Day Off' },
                      annual_leave: { bg: 'bg-gradient-to-br from-emerald-400 to-green-600', text: 'text-white', icon: <Palmtree className="w-5 h-5" />, label: 'Annual' }
                    };
                    
                    const style = entry ? (entryStyles[entry.entry_type] || entryStyles.shift) : null;
                    
                    return (
                      <div 
                        key={day} 
                        className={`rounded-xl overflow-hidden shadow-md transition-all hover:shadow-lg hover:scale-[1.02] ${isToday ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}`}
                      >
                        {/* Day Header */}
                        <div className={`px-3 py-2 text-center ${isToday ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white' : 'bg-slate-100 text-slate-700'}`}>
                          <p className="font-bold text-sm">{day}</p>
                          <p className={`text-xs ${isToday ? 'text-white/80' : 'text-slate-400'}`}>{format(getDateForDay(idx), 'MMM d')}</p>
                          {isToday && <span className="text-[10px] font-medium bg-white/20 px-1.5 py-0.5 rounded-full">TODAY</span>}
                        </div>
                        
                        {/* Entry Content */}
                        <div className={`p-3 min-h-[80px] flex flex-col items-center justify-center ${entry ? style.bg : 'bg-white'}`}>
                          {entry ? (
                            <div className={`text-center ${style.text}`}>
                              {style.icon}
                              <p className="font-bold mt-1">
                                {entry.entry_type === 'shift' || entry.entry_type === 'holiday_worked' 
                                  ? entry.start_time 
                                  : (style.label || entry.entry_type.replace(/_/g, ' '))}
                              </p>
                              {entry.comment && (
                                <p className="text-xs opacity-80 mt-1 flex items-center justify-center gap-1">
                                  <MessageSquare className="w-3 h-3" />
                                  <span className="truncate max-w-[80px]">{entry.comment}</span>
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="text-center text-slate-400">
                              <CalendarOff className="w-6 h-6 mx-auto opacity-40" />
                              <p className="text-xs mt-1">No schedule</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions Footer */}
                        {canRequest && entry?.entry_type === 'shift' && !pendingRequest && (
                          <div className="px-2 py-1.5 bg-slate-50 border-t flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-7 px-2 text-indigo-600 hover:bg-indigo-50"
                              onClick={() => openRequestDialog(idx)}
                              data-testid={`request-change-${idx}`}
                            >
                              Request Change
                            </Button>
                            {getSwappableCoworkers().length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-orange-600 hover:bg-orange-50"
                                onClick={() => openSwapDialog(entry)}
                                title="Request shift swap"
                                data-testid={`swap-btn-${idx}`}
                              >
                                <ArrowLeftRight className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                        {pendingRequest && (
                          <div className="px-2 py-1.5 bg-amber-50 border-t text-center">
                            <Badge className="bg-amber-500 text-white text-xs">Request Pending</Badge>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Requests */}
        {myRequests.length > 0 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <Send className="w-5 h-5" />
                My Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {myRequests.slice(0, 5).map((request) => (
                  <div 
                    key={request.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        {request.date} - Request {request.requested_start_time} start
                      </p>
                      {request.reason && (
                        <p className="text-xs text-slate-500">"{request.reason}"</p>
                      )}
                    </div>
                    <Badge 
                      variant={request.status === 'approved' ? 'default' : request.status === 'rejected' ? 'destructive' : 'secondary'}
                      className={request.status === 'approved' ? 'bg-green-600' : ''}
                    >
                      {request.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Legend */}
        <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="py-3">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-400 to-blue-600 shadow-sm"></div>
                <span className="text-slate-600 font-medium">Shift</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 shadow-sm"></div>
                <span className="text-slate-600 font-medium">Public Holiday</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-gradient-to-br from-purple-400 to-purple-600 shadow-sm"></div>
                <span className="text-slate-600 font-medium">RDO</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-gradient-to-br from-red-400 to-red-600 shadow-sm"></div>
                <span className="text-slate-600 font-medium">Sick</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-gradient-to-br from-emerald-400 to-green-600 shadow-sm"></div>
                <span className="text-slate-600 font-medium">Leave</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Request Start Time Change</DialogTitle>
            <DialogDescription>
              {selectedDate?.date && format(parseISO(selectedDate.date), 'dd/MM/yyyy')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Requested Start Time</Label>
              <Input
                type="time"
                value={requestForm.requested_start_time}
                onChange={(e) => setRequestForm(prev => ({ ...prev, requested_start_time: e.target.value }))}
                data-testid="request-start-time"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                placeholder="Why do you need this change?"
                value={requestForm.reason}
                onChange={(e) => setRequestForm(prev => ({ ...prev, reason: e.target.value }))}
                data-testid="request-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitRequest}
              disabled={submitting}
              className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
              data-testid="submit-request-btn"
            >
              {submitting ? <div className="spinner" /> : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Swap Request Dialog */}
      <Dialog open={showSwapDialog} onOpenChange={setShowSwapDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-orange-600" />
              Request Shift Swap
            </DialogTitle>
            <DialogDescription>
              Select a coworker to swap your shift with. They must approve, then a manager.
            </DialogDescription>
          </DialogHeader>
          
          {selectedSwapEntry && (
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-sm text-blue-600">Your shift</p>
              <p className="font-mono font-bold text-blue-800">
                {selectedSwapEntry.date} at {selectedSwapEntry.start_time}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Swap with</Label>
              <Select value={swapTargetUserId} onValueChange={setSwapTargetUserId}>
                <SelectTrigger data-testid="swap-target-select">
                  <SelectValue placeholder="Select a coworker..." />
                </SelectTrigger>
                <SelectContent>
                  {getSwappableCoworkers().map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSwapDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitSwap}
              disabled={swapSubmitting || !swapTargetUserId}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="submit-swap-btn"
            >
              {swapSubmitting ? <div className="spinner" /> : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Swaps Dialog (for coworker approval) */}
      <Dialog open={showSwapsListDialog} onOpenChange={setShowSwapsListDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-orange-600" />
              Shift Swap Requests
            </DialogTitle>
            <DialogDescription>
              Your coworkers want to swap shifts with you
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px]">
            {getPendingSwapsForMe().length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No pending swap requests
              </div>
            ) : (
              <div className="space-y-3">
                {getPendingSwapsForMe().map((swap) => (
                  <div 
                    key={swap.id} 
                    className="p-4 bg-orange-50 border border-orange-200 rounded-lg"
                    data-testid={`swap-request-${swap.id}`}
                  >
                    <div className="text-center space-y-2">
                      <p className="font-medium">{swap.requester_name}</p>
                      <p className="text-sm text-slate-600">
                        wants to swap their shift on <span className="font-mono">{swap.date}</span>
                      </p>
                    </div>
                    <div className="flex justify-center gap-2 mt-3">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleRespondToSwap(swap.id, 'approve')}
                        data-testid={`approve-swap-${swap.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRespondToSwap(swap.id, 'reject')}
                        data-testid={`reject-swap-${swap.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Notification Panel */}
      <NotificationPanel 
        open={showNotifications} 
        onClose={() => setShowNotifications(false)}
        onRead={() => setUnreadCount(prev => Math.max(0, prev - 1))}
      />

      {/* Annual Leave Request Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palmtree className="w-5 h-5 text-green-600" />
              Request Annual Leave
            </DialogTitle>
            <DialogDescription>
              Submit a leave request for approval by your manager.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="leave-start">Start Date</Label>
                <Input
                  id="leave-start"
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm(prev => ({ ...prev, start_date: e.target.value }))}
                  data-testid="leave-start-date"
                />
              </div>
              <div>
                <Label htmlFor="leave-end">End Date</Label>
                <Input
                  id="leave-end"
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm(prev => ({ ...prev, end_date: e.target.value }))}
                  min={leaveForm.start_date}
                  data-testid="leave-end-date"
                />
              </div>
            </div>
            
            {leaveForm.start_date && leaveForm.end_date && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700">
                  Total: <span className="font-semibold">
                    {Math.ceil((new Date(leaveForm.end_date) - new Date(leaveForm.start_date)) / (1000 * 60 * 60 * 24)) + 1} day(s)
                  </span>
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="leave-reason">Reason (optional)</Label>
              <Textarea
                id="leave-reason"
                placeholder="Holiday, family event, etc."
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                data-testid="leave-reason"
                rows={3}
              />
            </div>

            {/* My pending leave requests */}
            {myLeaveRequests.filter(r => r.status === 'pending').length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Your Pending Requests:</p>
                <div className="space-y-2">
                  {myLeaveRequests.filter(r => r.status === 'pending').map(request => (
                    <div key={request.id} className="flex items-center justify-between p-2 bg-amber-50 rounded border border-amber-200">
                      <div className="text-sm">
                        <span className="font-mono">{request.start_date}</span>
                        <span className="text-slate-400 mx-1">→</span>
                        <span className="font-mono">{request.end_date}</span>
                        <span className="text-slate-500 ml-2">({request.total_days} days)</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleCancelLeave(request.id)}
                        data-testid={`cancel-leave-${request.id}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitLeave}
              disabled={leaveSubmitting || !leaveForm.start_date || !leaveForm.end_date}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-leave-btn"
            >
              {leaveSubmitting ? <div className="spinner" /> : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeRoster;
